import { useEffect } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Divider, List, Stack, Typography } from '@mui/material';

import {
  AssessmentOutlined,
  CloudDownloadOutlined,
  DescriptionOutlined,
  DevicesOutlined,
  GppGoodOutlined,
  HomeOutlined,
  ListAlt,
  NotificationsActiveOutlined,
  SettingsOutlined,
} from '@mui/icons-material';

import { SIDEBAR_WIDTH } from '@/styles/constant';
import { MenuItem, MenuItemWithChild } from './MenuItem';

import type { MenuItemData } from '@/@types/types';

export const selectedMenuAtom = atom(window.location.pathname);
export const fdaEnabledAtom = atom(false);

export default function Sidebar() {
  const setSelectedMenu = useSetAtom(selectedMenuAtom);
  // TODO: Get FDA enable status from API
  const fdaEnabled = useAtomValue(fdaEnabledAtom);

  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedMenu(location.pathname);
  }, [location.pathname, setSelectedMenu]);

  const fdaItems: MenuItemData[] = [
    { text: t('FDA.label.endorsementManagement'), icon: null, path: '/fda/endorsement' },
    { text: t('FDA.label.electronicSignature'), icon: null, path: '/fda/signature' },
  ];
  const settingItems: MenuItemData[] = [
    { text: t('Common.label.users'), icon: null, path: '/setting/users' },
    { text: t('Common.label.members'), icon: null, path: '/setting/members' },
    { text: t('Common.label.groups'), icon: null, path: '/setting/groups' },
    { text: t('Common.label.subscription'), icon: null, path: '/setting/subscription' },
  ];
  const menuItems: MenuItemData[] = [
    { text: t('Common.label.home'), icon: <HomeOutlined />, path: '/home' },
    { text: t('Common.label.devices'), icon: <DevicesOutlined />, path: '/devices' },
    { text: t('Common.label.projects'), icon: <ListAlt />, path: '/projects' },
    { text: t('FDA.label.fda'), icon: <GppGoodOutlined />, path: '/fda', child: fdaItems, disabled: !fdaEnabled },
    { text: t('Common.label.alarmHistory'), icon: <NotificationsActiveOutlined />, path: '/alarm-history' },
    { text: t('Log.label.operationLog'), icon: <DescriptionOutlined />, path: '/operation-log' },
    { text: t('Common.label.reports'), icon: <AssessmentOutlined />, path: '/reports' },
    { text: t('Common.label.settings'), icon: <SettingsOutlined />, path: '/setting', child: settingItems },
  ];

  return (
    <Stack
      sx={{
        flexShrink: 0,
        width: SIDEBAR_WIDTH,
        height: '100%',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      <List disablePadding>
        {menuItems.map((item) =>
          item.child ? <MenuItemWithChild key={item.path} item={item} /> : <MenuItem key={item.path} item={item} />,
        )}

        <Divider />
      </List>
      <Button onClick={() => window.open('/software')} sx={{ mt: 'auto', px: 2 }}>
        <CloudDownloadOutlined sx={{ marginRight: 2 }} />
        <Stack>
          <Typography variant="caption" align="left" fontWeight="bold" lineHeight={1.3}>
            Download LogTrack USB Temperature Data Logger PC Tool
          </Typography>
          <Typography variant="caption" align="left">
            (LogTrack Connect)
          </Typography>
        </Stack>
      </Button>
    </Stack>
  );
}
